import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { countAppEvent, countFirstOpen } from './appEvents';
import { trackEvent } from './gt';
import { installState, onAppInstalled, promptInstall, subscribeInstall } from './installPrompt';
import {
  canOfferInstall,
  installPlatformOf,
  isStandalone,
  platformLabel,
  promptCapable,
  readInstallDismissedAt,
  writeInstallDismissed,
} from './pwa';

const userAgent = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent);
const touchPoints = () => (typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0);
const promptOf = () => installState().prompt;
const installedOf = () => installState().installed;

/**
 * The installed app's doors, for InstallAppProvider: the browser's install prompt captured in main.jsx, the banner's ✕
 * (30 days), the steps sheet, and the counts: an install tap, the browser saying it installed, and every launch of the
 * installed app. The page decides what shows where (components/app/InstallBanner, the footer, Conta).
 */
export const useInstallAppState = () => {
  const prompt = useSyncExternalStore(subscribeInstall, promptOf, () => null);
  const installed = useSyncExternalStore(subscribeInstall, installedOf, () => false);
  const [standalone] = useState(() => isStandalone());
  const [dismissedAt, setDismissedAt] = useState(() => readInstallDismissedAt());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetLook, setSheetLook] = useState('ios');
  // Tapped "Instalar" on the banner: it goes for this page load (the ✕ or closing the steps hides it for 30 days)
  const [tapped, setTapped] = useState(false);
  // A browser that announces the install prompt waits for it, so the banner shows once, in its final look
  const [canPrompt] = useState(() => promptCapable());
  const platform = installPlatformOf(userAgent(), prompt != null, touchPoints(), canPrompt);
  const label = platformLabel(platform, userAgent());
  const running = standalone || installed;

  useEffect(
    () =>
      onAppInstalled(() => {
        const installedOn = platformLabel('prompt', userAgent());
        trackEvent('app_installed', `App - Installed: ${installedOn}`, 'pwa', true);
        countAppEvent('installed', installedOn);
      }),
    []
  );

  useEffect(() => {
    if (!standalone) return;
    const openedOn = platformLabel(installPlatformOf(userAgent(), false, touchPoints()), userAgent());
    trackEvent('app_opened', `App - Opened: ${openedOn}`, 'pwa', true);
    // Every platform, the iPhone included (it never fires appinstalled): the installs that were really opened
    countFirstOpen(openedOn);
  }, [standalone]);

  const install = useCallback(
    (source) => {
      trackEvent('app_install_clicked', `App - Install Clicked: ${source}`, 'pwa', true);
      countAppEvent('install-click', label, source);
      if (source === 'banner') setTapped(true);
      if (platform === 'prompt') promptInstall();
      else if (platform === 'ios' || platform === 'android') {
        // The iPhone's steps; on Android, the browser's menu while Chrome's prompt isn't in yet
        setSheetLook(platform);
        setSheetOpen(true);
      }
    },
    [platform, label]
  );

  const dismissBanner = useCallback(() => {
    const now = Date.now();
    writeInstallDismissed(now);
    setDismissedAt(now);
  }, []);

  // Having seen the steps is an answer too: a phone never tells the browser they went through with it, so closing them
  // counts like the ✕
  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    dismissBanner();
  }, [dismissBanner]);

  const installNow = useMemo(
    () =>
      sheetLook === 'android' && platform === 'prompt'
        ? () => {
            setSheetOpen(false);
            promptInstall();
          }
        : null,
    [sheetLook, platform]
  );

  const state = useMemo(
    () => ({ platform, standalone: running, canOffer: canOfferInstall(platform, running), dismissedAt, tapped, install, dismissBanner }),
    [platform, running, dismissedAt, tapped, install, dismissBanner]
  );
  const sheet = useMemo(
    () => ({ open: sheetOpen, look: sheetLook, onInstallNow: installNow, onClose: closeSheet }),
    [sheetOpen, sheetLook, installNow, closeSheet]
  );

  return { state, sheet };
};
